import * as React from 'react';
import {
    Alert, Box, Button, Card, CardContent, CircularProgress, Dialog, DialogActions, DialogContent,
    DialogTitle, Grid, Stack, Tab, Tabs, Typography,
} from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import { getVirtualStatistics } from '../../Api';
import {
    VirtualStatisticsCountsDto, VirtualStatisticsDto, VirtualStatisticsRange,
} from '../../models/dtos/VirtualProviderCatalogDto';

interface VirtualStatisticsDialogProps {
    open: boolean;
    onClose: () => void;
}

const RANGES: { range: VirtualStatisticsRange; label: string }[] = [
    { range: 'LAST_HOUR', label: 'Last hour' },
    { range: 'LAST_12_HOURS', label: 'Last 12 hours' },
    { range: 'TODAY', label: 'Today' },
    { range: 'YESTERDAY', label: 'Yesterday' },
    { range: 'LAST_WEEK', label: 'Last week' },
];

const SECONDS_PER_DAY = 86400;
const numberFormatter = new Intl.NumberFormat();

/** Daily buckets are labelled by date; shorter ones by the time their interval opens. */
function bucketLabel(startsAt: string, bucketSeconds: number) {
    const date = new Date(startsAt);
    return bucketSeconds >= SECONDS_PER_DAY
        ? date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
        : date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

function StatTile(props: { label: string; value: number }) {
    return (
        <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
                <Typography variant="h4">{numberFormatter.format(props.value)}</Typography>
                <Typography variant="body2" color="text.secondary">{props.label}</Typography>
            </CardContent>
        </Card>
    );
}

function tiles(totals: VirtualStatisticsCountsDto) {
    return [
        { label: 'Leagues created', value: totals.leaguesCreated },
        { label: 'Teams created', value: totals.teamsCreated },
        { label: 'Players created', value: totals.playersCreated },
        { label: 'Matches created', value: totals.matchesCreated },
        { label: 'Matches finished', value: totals.matchesFinished },
    ];
}

function StatisticsCharts({ statistics }: { statistics: VirtualStatisticsDto }) {
    const labels = statistics.buckets.map(bucket => bucketLabel(bucket.startsAt, statistics.bucketSeconds));
    const axis = [{ scaleType: 'band' as const, data: labels }];

    return (
        <Stack spacing={3}>
            <Box>
                <Typography variant="subtitle1">Catalog created</Typography>
                <BarChart
                    xAxis={axis}
                    series={[
                        { data: statistics.buckets.map(b => b.counts.leaguesCreated), label: 'Leagues', stack: 'created' },
                        { data: statistics.buckets.map(b => b.counts.teamsCreated), label: 'Teams', stack: 'created' },
                        { data: statistics.buckets.map(b => b.counts.playersCreated), label: 'Players', stack: 'created' },
                    ]}
                    height={260}
                />
            </Box>
            <Box>
                <Typography variant="subtitle1">Matches</Typography>
                <BarChart
                    xAxis={axis}
                    series={[
                        { data: statistics.buckets.map(b => b.counts.matchesCreated), label: 'Created' },
                        { data: statistics.buckets.map(b => b.counts.matchesFinished), label: 'Finished' },
                    ]}
                    height={260}
                />
            </Box>
        </Stack>
    );
}

export default function VirtualStatisticsDialog({ open, onClose }: VirtualStatisticsDialogProps) {
    const [range, setRange] = React.useState<VirtualStatisticsRange>('LAST_HOUR');
    const [statistics, setStatistics] = React.useState<VirtualStatisticsDto | null>(null);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState('');

    React.useEffect(() => {
        if (!open) return;
        let cancelled = false;
        setLoading(true);
        setError('');
        getVirtualStatistics(range)
            .then(loaded => { if (!cancelled) setStatistics(loaded); })
            .catch(reason => {
                if (cancelled) return;
                setStatistics(null);
                setError(reason instanceof Error ? reason.message : 'Could not load statistics');
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [open, range]);

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>Statistics</DialogTitle>
            <DialogContent dividers>
                <Tabs
                    value={range}
                    onChange={(_, next: VirtualStatisticsRange) => setRange(next)}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{ mb: 2 }}
                >
                    {RANGES.map(tab => <Tab key={tab.range} value={tab.range} label={tab.label} />)}
                </Tabs>

                {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

                {loading ? <Box textAlign="center" py={6}><CircularProgress /></Box> : null}

                {!loading && statistics ? (
                    <Stack spacing={3}>
                        <Typography variant="body2" color="text.secondary">
                            {new Date(statistics.from).toLocaleString()} — {new Date(statistics.to).toLocaleString()}
                        </Typography>
                        <Grid container spacing={2}>
                            {tiles(statistics.totals).map(tile => (
                                <Grid item xs={6} sm={4} md={2.4} key={tile.label}>
                                    <StatTile label={tile.label} value={tile.value} />
                                </Grid>
                            ))}
                        </Grid>
                        <StatisticsCharts statistics={statistics} />
                    </Stack>
                ) : null}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}
